/**
 * EPIC-DM01-F: Demo Activation Hook
 * Handles token validation and account activation flow
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ActivationStartResponse {
  ok: boolean;
  masked_email?: string;
  prospect_name?: string;
  next_step?: 'set_password' | 'login' | 'already_activated';
  session_id?: string;
  error?: string;
  message?: string;
}

export interface ActivationCompleteResponse {
  ok: boolean;
  redirect_url?: string;
  email?: string;
  error?: string;
  message?: string;
  validation_errors?: string[];
}

export type ActivationState = 
  | 'idle'
  | 'validating'
  | 'valid'
  | 'invalid'
  | 'setting_password'
  | 'completing'
  | 'completed'
  | 'error';

// API helper for public endpoints (no auth required)
async function callPublicEdgeFunction<T>(
  functionName: string,
  body: unknown
): Promise<T> {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  
  if (!response.ok) {
    const error = new Error(data.message || data.error || `Request failed with status ${response.status}`);
    (error as any).code = data.error;
    (error as any).validationErrors = data.validation_errors;
    throw error;
  }

  return data as T;
}

export function useDemoActivation() {
  const [state, setState] = useState<ActivationState>('idle');
  const [error, setError] = useState<Error | null>(null);
  const [tokenData, setTokenData] = useState<ActivationStartResponse | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  /**
   * Validate activation token and get next step
   */
  const startActivation = useCallback(async (token: string): Promise<ActivationStartResponse> => {
    setState('validating');
    setError(null);
    
    try {
      const result = await callPublicEdgeFunction<ActivationStartResponse>(
        'demo-activate-start',
        { token }
      );
      
      if (result.ok) {
        setTokenData(result);
        setState('valid');
        
        console.log('[useDemoActivation] Token validated:', {
          event: 'demo_activation_token_validated',
          next_step: result.next_step,
        });
      } else {
        setState('invalid');
        setError(new Error(result.message || 'Invalid or expired token'));
      }
      
      return result;
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to validate token');
      setState('invalid');
      setError(errorObj);
      
      console.error('[useDemoActivation] Token validation failed:', err);
      
      return {
        ok: false,
        error: 'validation_failed',
        message: errorObj.message,
      };
    }
  }, []);

  /**
   * Complete activation by setting password
   */
  const completeActivation = useCallback(async (
    token: string, 
    password: string
  ): Promise<ActivationCompleteResponse> => {
    setState('completing');
    setError(null);
    
    try {
      const result = await callPublicEdgeFunction<ActivationCompleteResponse>(
        'demo-activate-complete',
        { token, password }
      );
      
      if (result.ok) {
        setState('completed');
        setRedirectUrl(result.redirect_url || '/dashboard');
        
        console.log('[useDemoActivation] Activation completed:', {
          event: 'demo_activation_completed',
          has_redirect: !!result.redirect_url,
        });
      } else {
        setState('error');
        const validationMsg = result.validation_errors?.join('. ') || result.message;
        setError(new Error(validationMsg || 'Activation failed'));
      }
      
      return result;
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to complete activation');
      setState('error');
      setError(errorObj);
      
      console.error('[useDemoActivation] Activation failed:', err);
      
      return {
        ok: false,
        error: 'activation_failed',
        message: errorObj.message,
        validation_errors: (err as any).validationErrors,
      };
    }
  }, []);

  /**
   * Reset state for retry
   */
  const reset = useCallback(() => {
    setState('idle');
    setError(null);
    setTokenData(null);
    setRedirectUrl(null);
  }, []);

  return {
    // State
    state,
    error,
    tokenData,
    redirectUrl,
    
    // Computed
    isValidating: state === 'validating',
    isValid: state === 'valid',
    isInvalid: state === 'invalid',
    isCompleting: state === 'completing',
    isCompleted: state === 'completed',
    hasError: state === 'error' || state === 'invalid',
    
    // Actions
    startActivation,
    completeActivation,
    reset,
  };
}
